import React, { useMemo } from 'react';

import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { Typography } from '@scale/scaleui';

import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { CORP_LOOKUP_PATH, REMOADMIN_LOOKUP_PATH } from '../constants';
import { useSensorFusionLookupContext } from '../contexts/SensorFusionLookupContext';
import {
  StageDefinitionLinkDto,
  SubstageDefinitionLinkDto,
} from '@scale/shared/sensorFusion/corpLookupLinkDto';
import { SubstageInstanceStatusValues } from '@scale/shared-types';

type SfuStagesPanelProps = {
  stageDefinitions: (StageDefinitionLinkDto & {
    statusCounts: {
      claimed: { [key in SubstageInstanceStatusValues]: number };
      unclaimed: { [key in SubstageInstanceStatusValues]: number };
    };
    substageDefinitions: (SubstageDefinitionLinkDto & {
      dependencies?: string[];
    })[];
    dependencies?: string[];
  })[];
};

const SfuStagesPanel = ({ stageDefinitions }: SfuStagesPanelProps) => {
  const { isRemoAdmin } = useRemoAdminCtx();
  const { scaleDashboardFrontendUrl, remotasksFrontendUrl } = useSensorFusionLookupContext() ?? {};

  const toolUrl = useMemo(() => {
    if (isRemoAdmin) {
      return `${remotasksFrontendUrl ?? ''}/${REMOADMIN_LOOKUP_PATH}`;
    }
    return `${scaleDashboardFrontendUrl ?? ''}/${CORP_LOOKUP_PATH}`;
  }, [isRemoAdmin, remotasksFrontendUrl, scaleDashboardFrontendUrl]);

  return (
    <div className="border border-solid border-neutral-200 px-4 py-4 flex flex-col gap-2">
      <Typography.H3>Stages Definitions</Typography.H3>
      <div className="flex flex-col">
        {stageDefinitions.length ? (
          <ul className="ml-2">
            {stageDefinitions.map(stageDefinition => (
              <li className="flex flex-col" key={stageDefinition.id}>
                <details>
                  <summary>
                    <b className="mr-2">{`${stageDefinition.name} `}</b>
                    <WithCopy className="inline-block text-info-700" copyText={stageDefinition.id}>
                      <a href={`${toolUrl}/${stageDefinition.id}`}>{stageDefinition.id}</a>
                    </WithCopy>
                  </summary>
                  <div>
                    <span className="ml-2 font-medium">Substages definitions:</span>
                    {stageDefinition.substageDefinitions.length ? (
                      <ul className="ml-4">
                        {stageDefinition.substageDefinitions.map(substageDefinition => (
                          <li className="flex flex-col" key={substageDefinition.id}>
                            <div>
                              {`- ${substageDefinition.name} `}
                              <WithCopy
                                className="inline-block text-info-700"
                                copyText={substageDefinition.id}
                              >
                                <a href={`${toolUrl}/${substageDefinition.id}`}>
                                  {substageDefinition.id}
                                </a>
                              </WithCopy>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="ml-2">None</span>
                    )}
                  </div>
                </details>
              </li>
            ))}
          </ul>
        ) : (
          <span className="ml-2">None</span>
        )}
      </div>
      <div className="flex flex-col"></div>
    </div>
  );
};

export default SfuStagesPanel;
